// Paying from a mini app that the messenger renders inside an iframe.
//
// The web clients of Eitaa and Bale (web.eitaa.com, web.bale.ai) do not give
// us a webview: they put the mini app in an <iframe> on their own page. That
// is how every iPhone user of either messenger runs us — in Safari — and how
// every desktop user does too.
//
// A bank gateway cannot load in there. Our own bridge pages (hamyanapp.com,
// gethamyan.ir) answer `X-Frame-Options: SAMEORIGIN`, card.gethamyan.ir answers
// `DENY`, the shaparak pages behave the same, and Safari refuses the gateway's
// cross-site cookies in a frame on top of that. So `location.replace(link)`
// inside the frame leaves the payer on a blank frame, whatever their phone's
// settings. PostHog: every `payment_gateway_redirect` sent from
// `miniapp_client=web` in the 60 days to 2026-09-20 — iOS, Windows, Mac; Bale
// and Eitaa; ASP_IPG, PSG_IPG and HAM_IPG — ended without a payment.
//
// What this module does instead, only when we are framed:
//
//   1. The gateway opens in a NEW TAB of the real browser (`window.open`), via
//      a one-line hop through our own origin — `/#gw_handoff=<link>` — that
//      marks the tab as "a payment that came from a mini app" before sending
//      it on to the bank (`takeGatewayHandoff`).
//   2. The bank returns that tab to `/funds/?result=…` as usual. Seeing the
//      mark, the tab renders a small result screen instead of the app — it
//      could not render the app anyway: its storage is not the frame's, so it
//      has no session — and offers «بازگشت به ایتا/بله» (`readGatewayReturn`).
//   3. That screen hands the verdict to the frame that opened it
//      (`window.opener.postMessage`) and closes itself, which puts the payer
//      back on the messenger tab, where the mini app moves to /funds with the
//      verdict and counts it exactly like a normal return.
//
// Outside a frame — native webviews, the plain PWA — nothing here runs and the
// redirect is the same `location.replace` as always.

import posthog from 'posthog-js'
import { getClientPlatform, resolvePlatform } from 'modules/miniapp/detect'
import type { MiniappPlatform } from 'modules/miniapp/detect'
import { EITAA_MINIAPP_URL } from 'modules/landing/links'

/** The fragment key that turns a page load into a hop to the bank. */
export const HANDOFF_PARAM = 'gw_handoff'
const FROM_PARAM = 'gw_from'

/** sessionStorage: this tab went to a bank on behalf of a framed mini app. */
const MARKER_KEY = 'gateway:handoff'

/**
 * How long a payment may take at the bank and still come back to the result
 * screen. The gateway pages themselves time out in 10–20 minutes; this only has
 * to be longer than that and short enough that a tab left open overnight goes
 * back to being an ordinary page.
 */
export const MARKER_MAX_AGE_MS = 2 * 60 * 60 * 1000

/** The message type the result tab posts to the frame that opened it. */
export const RESULT_MESSAGE = 'hamyan:gateway-result'

/** The start param a «بازگشت» deep link carries, so its launches can be counted. */
export const BACK_START_PARAM = 'gwback'

/**
 * Hosts a payment link may point at. Hopping through our origin makes the hop
 * page a redirector, and a redirector that follows any URL in its fragment is
 * an open redirect with our name on it — so it only follows these. Everything
 * the backend hands out today is on one of them: our bridge pages
 * (hamyanapp.com, gethamyan.ir, card.gethamyan.ir) and the banks' own shaparak
 * pages.
 */
const GATEWAY_HOSTS = [
    'hamyanapp.com',
    'gethamyan.ir',
    'shaparak.ir',
    'sep.ir',
    'asanpardakht.ir',
    'payping.ir',
    'zarinpal.com',
]

export const isGatewayLink = (link: string): boolean => {
    try {
        const url = new URL(link)
        const host = url.hostname.toLowerCase()
        return (
            url.protocol === 'https:' &&
            GATEWAY_HOSTS.some((h) => host === h || host.endsWith(`.${h}`))
        )
    } catch {
        return false
    }
}

/** True when this document is inside someone else's page. */
export const isFramed = (): boolean => {
    try {
        return window.self !== window.top
    } catch {
        // Reading `top` across origins is allowed; a browser that throws on it
        // is certainly framing us.
        return true
    }
}

const hostOf = (link: string): string | null => {
    try {
        return new URL(link).hostname
    } catch {
        return null
    }
}

/** The address of the hop page for a given payment link. */
export const handoffUrl = (
    link: string,
    from: MiniappPlatform | null
): string => {
    const params = new URLSearchParams()
    params.set(HANDOFF_PARAM, link)
    if (from) params.set(FROM_PARAM, from)
    // The fragment, not the query: a query string would be written into the
    // CDN's access log with the payment token in it. The fragment never
    // leaves the browser.
    return `${window.location.origin}/#${params.toString()}`
}

// ---------------------------------------------------------------------------
// The framed side: the mini app that wants to send a payer to the bank.

export interface PendingHandoff {
    /** what the new tab is pointed at — the hop page, or the bare link */
    url: string
    /** the backend's payment link itself */
    link: string
    /** whether a tab actually opened; false = the browser blocked it */
    opened: boolean
    at: number
}

let pending: PendingHandoff | null = null
const listeners = new Set<() => void>()

const publish = (next: PendingHandoff | null) => {
    pending = next
    listeners.forEach((listener) => listener())
}

export const getPendingHandoff = (): PendingHandoff | null => pending
export const subscribeHandoff = (listener: () => void): (() => void) => {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}
export const clearPendingHandoff = () => publish(null)

/**
 * `window.open`, returning whether a tab actually opened.
 *
 * Deliberately without `noopener`: the result tab needs `window.opener` to hand
 * the verdict back, and without it the only way home would be a deep link that
 * opens a second copy of the messenger. The pages it can reach are our own and
 * the banks' (see `GATEWAY_HOSTS`).
 */
const openTab = (url: string): boolean => {
    try {
        return window.open(url, '_blank') != null
    } catch {
        return false
    }
}

const describe = (link: string) => ({
    gateway_host: hostOf(link),
    miniapp_platform: resolvePlatform(),
    miniapp_client: getClientPlatform(),
})

/**
 * Send the payer to a gateway — the one way every pay flow leaves the app.
 *
 * Unframed, this is exactly the `window.location.replace(link)` each flow used
 * to do itself. Framed, it opens the gateway in a new tab; when the browser
 * blocks that (the tap that started it is usually too old by the time the
 * server has answered — Safari in particular), `GatewayHandoffSheet` puts up a
 * button, and the tap on that button opens it.
 */
export const goToGateway = (link: string | null | undefined): void => {
    if (!link) return
    if (!isFramed()) {
        window.location.replace(link)
        return
    }

    const url = isGatewayLink(link) ? handoffUrl(link, resolvePlatform()) : link
    const opened = openTab(url)
    posthog.capture('gateway_external_open', {
        ...describe(link),
        opened,
        via: 'auto',
        return_screen: url !== link,
    })
    publish({ url, link, opened, at: Date.now() })
}

/**
 * Open the pending gateway again, from a tap.
 *
 * Called synchronously inside a click handler: that is what makes a popup
 * blocker let it through when the automatic attempt was refused.
 */
export const reopenGateway = (): boolean => {
    if (!pending) return false
    const opened = openTab(pending.url)
    posthog.capture('gateway_external_open', {
        ...describe(pending.link),
        opened,
        via: 'tap',
        return_screen: pending.url !== pending.link,
    })
    publish({ ...pending, opened: pending.opened || opened })
    return opened
}

// ---------------------------------------------------------------------------
// The new tab: first the hop to the bank, then the way back.

interface Marker {
    from: MiniappPlatform | null
    at: number
}

const asPlatform = (value: string | null): MiniappPlatform | null =>
    value === 'bale' || value === 'eitaa' || value === 'telegram'
        ? value
        : null

/**
 * If this page load is the hop, send the tab to the bank and return true.
 *
 * Must run before anything renders: the caller skips the whole app when it
 * returns true, so no pageview carries the payment link off to analytics and
 * no service worker is installed in a tab that will not stay.
 */
export const takeGatewayHandoff = (): boolean => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const link = hash.get(HANDOFF_PARAM)
    if (!link) return false

    if (!isGatewayLink(link)) {
        // Not ours to follow. Drop the fragment and carry on as a normal page.
        window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search
        )
        return false
    }

    try {
        const marker: Marker = {
            from: asPlatform(hash.get(FROM_PARAM)),
            at: Date.now(),
        }
        // sessionStorage: it belongs to this one tab, and it survives the trip
        // through the bank's pages and back to our origin.
        window.sessionStorage.setItem(MARKER_KEY, JSON.stringify(marker))
    } catch {
        // No marker only means the tab comes back to the ordinary app instead
        // of the result screen. The payment itself is unaffected.
    }
    window.location.replace(link)
    return true
}

const readMarker = (): Marker | null => {
    try {
        const raw = window.sessionStorage.getItem(MARKER_KEY)
        if (!raw) return null
        const marker: Marker = JSON.parse(raw)
        if (!marker?.at || Date.now() - marker.at > MARKER_MAX_AGE_MS) {
            return null
        }
        return { from: asPlatform(marker.from), at: marker.at }
    } catch {
        return null
    }
}

export const clearGatewayMarker = () => {
    try {
        window.sessionStorage.removeItem(MARKER_KEY)
    } catch {
        /* nothing to clear */
    }
}

export interface GatewayOutcome {
    result: string
    code: string | null
    amount: number | null
    gateway: string | null
}

export interface GatewayReturnTab {
    from: MiniappPlatform | null
    outcome: GatewayOutcome
}

/**
 * The bank has brought a handed-off tab back: what it said, and who to return
 * the payer to. Null for every other page load.
 *
 * `/funds/?result=…` is where every v2 gateway lands a PWA payer. The legacy v1
 * target `/gateway-callback/` carries no verdict at all, so it reads as
 * `unknown` — never as a failure, for the same reason the legacy page itself
 * refuses to call it one.
 */
export const readGatewayReturn = (): GatewayReturnTab | null => {
    const marker = readMarker()
    if (!marker) return null

    const params = new URLSearchParams(window.location.search)
    const result = params.get('result')
    const legacy = window.location.pathname.startsWith('/gateway-callback')
    if (!result && !legacy) return null

    return {
        from: marker.from,
        outcome: {
            result: result || 'unknown',
            code: params.get('code'),
            amount: Number(params.get('amount')) || null,
            gateway: params.get('gateway'),
        },
    }
}

/**
 * Hand the verdict to the mini app that opened this tab.
 *
 * The frame and this tab share an origin but not storage (the frame's is
 * partitioned under the messenger's site), so a message to `opener` is the only
 * channel between them. It is gone when a page on the way — a bank's — cut the
 * opener off; the mini app then learns nothing and falls back to «پرداخت را
 * تمام کردم».
 */
export const notifyOpener = (outcome: GatewayOutcome): boolean => {
    try {
        const opener = window.opener as Window | null
        if (!opener || opener === window || opener.closed) return false
        opener.postMessage(
            { type: RESULT_MESSAGE, ...outcome },
            window.location.origin
        )
        return true
    } catch {
        return false
    }
}

/** Where «بازگشت» goes when the tab cannot simply close itself. */
export const messengerLink = (from: MiniappPlatform | null): string | null => {
    switch (from) {
        case 'eitaa':
            return `${EITAA_MINIAPP_URL}?startapp=${BACK_START_PARAM}`
        case 'bale':
            return `https://ble.ir/hamyan_minibot?startapp=${BACK_START_PARAM}`
        case 'telegram':
            return `https://t.me/hamyan_minibot?startapp=${BACK_START_PARAM}`
        default:
            return null
    }
}

export const messengerName = (from: MiniappPlatform | null): string | null => {
    switch (from) {
        case 'eitaa':
            return 'ایتا'
        case 'bale':
            return 'بله'
        case 'telegram':
            return 'تلگرام'
        default:
            return null
    }
}

/**
 * Parse a message from a result tab. Anything that is not exactly ours — from
 * another origin, or of another shape — is null.
 */
export const readResultMessage = (
    event: MessageEvent
): GatewayOutcome | null => {
    if (event.origin !== window.location.origin) return null
    const data = event.data
    if (!data || typeof data !== 'object' || data.type !== RESULT_MESSAGE) {
        return null
    }
    if (typeof data.result !== 'string' || !data.result) return null
    const str = (v: unknown) => (typeof v === 'string' && v ? v : null)
    return {
        result: data.result,
        code: str(data.code),
        amount: typeof data.amount === 'number' ? data.amount : null,
        gateway: str(data.gateway),
    }
}

/** `/funds/?result=…` — the same address the backend itself sends a payer to. */
export const fundsReturnPath = (outcome: GatewayOutcome): string => {
    const params = new URLSearchParams()
    params.set('result', outcome.result)
    if (outcome.code) params.set('code', outcome.code)
    if (outcome.amount != null) params.set('amount', String(outcome.amount))
    if (outcome.gateway) params.set('gateway', outcome.gateway)
    return `/funds/?${params.toString()}`
}
